import { message } from "antd";
import db from "../../../database";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { getOAuthHeaders } from "../../../constants/oAuthValidation";
import i18next from "i18next";
import * as Sentry from "@sentry/react";
import upsertPOSLog from "../Retail/posLog";
import { createCustomerService, getCustomerService, updateCustomerService } from "../../../services";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import useCartStore from "../../../stores/useCartStore";
import useTransactionStore from "../../../stores/useTransactionStore";
import useReferenceStore from "../../../stores/useReferenceStore";
import useAuthStore from "../../../stores/useAuthStore";
import { getIsInterState } from "../../../utils/taxUtils";
import { groupItemsByTaxRate } from "./utils/taxCalculations";
import { useHistory } from "react-router-dom";

const baseUsedKeys = new Set([
  "mobile",
  "mobileno",
  "name",
  "email",
  "pincode",
  "lastname",
  "gender",
  "taxid",
  "anniversarydate",
  "birthday",
  "birthdaydate",
  "lastvisitdate",
  "validity",
  "type",
  "cardnumber",
  "program",
  "loyality",
]);

const hasContactAddressGroup = (retailCustomerGroups, retailCustomerTabs) => {
  const contactTabIds = (retailCustomerTabs || []).filter((tab) => tab.name?.toLowerCase().includes("contact")).map((tab) => tab.id);
  return (retailCustomerGroups || []).some((group) => contactTabIds.includes(group.tabId) && group.name?.toLowerCase().includes("address"));
};

const getUsedKeys = (retailCustomerGroups, retailCustomerTabs) => {
  if (hasContactAddressGroup(retailCustomerGroups, retailCustomerTabs)) {
    const keys = new Set(baseUsedKeys);
    keys.add("street");
    keys.add("city");
    return keys;
  }
  return baseUsedKeys;
};
const getCustomerConfigData = () => {
  const customerConfig = useReferenceStore.getState().customerConfigs || [];
  return JSON.parse(customerConfig[0]?.configuration || "[]");
};

const getFreshConfig = () => {
  const customerConfigData = getCustomerConfigData();
  const retailCustomerGroups = customerConfigData?.fieldGroups || [];
  const retailCustomerFields = customerConfigData?.fields || [];
  const retailCustomerTabs = customerConfigData?.tabs || [];
  const businessTabIds = retailCustomerTabs.filter((tab) => tab.name?.toLowerCase().includes("business")).map((tab) => tab.id);
  const businessGroupIds = retailCustomerGroups.filter((group) => businessTabIds.includes(group.tabId)).map((group) => group.id);
  return { customerConfigData, retailCustomerGroups, retailCustomerFields, retailCustomerTabs, businessTabIds, businessGroupIds };
};
const toCamelCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => (index === 0 ? match.toLowerCase() : match.toUpperCase()))
    .replace(/\s+/g, "");
};

const extractGroupData = (data, groupId) => {
  if (data[groupId]) return data[groupId];
  return data;
};

const parseGroupData = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value; // already parsed
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error("Invalid JSON string:", value);
      return {};
    }
  }
  return {};
};

const getBusinessAddressGroups = () => {
  const { retailCustomerTabs, retailCustomerGroups } = getFreshConfig();
  const businessTab = retailCustomerTabs.find((tab) => tab.name?.toLowerCase().includes("business"));

  if (!businessTab) return {};

  const businessGroups = retailCustomerGroups.filter((group) => group.tabId === businessTab.id);

  const invoiceGroup = businessGroups.find((g) => g.name?.toLowerCase().includes("invoic"));

  const deliveryGroup = businessGroups.find((g) => g.name?.toLowerCase().includes("deliver"));

  return { invoiceGroup, deliveryGroup };
};

const getCountryId = (countryValue, countriesData, fallbackCountryId) => {
  if (!countryValue) return fallbackCountryId;

  const byId = countriesData.find((country) => country.RecordID === countryValue);
  if (byId) return byId.RecordID;

  const byName = countriesData.find((country) => country.Name?.toLowerCase() === countryValue?.toLowerCase());
  if (byName) return byName.RecordID;

  return fallbackCountryId;
};

const buildCustomerListPayload = (
  data,
  selectedB2BCustomer,
  panCustomerInfo,
  tillData,
  gstInfo,
  isPanCustomer,
  isB2BEnabled,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  switchToggle
) => {
  const { invoiceGroup, deliveryGroup } = getBusinessAddressGroups();
  const rawInvoiceData = invoiceGroup ? data[invoiceGroup.id] : {};
  const rawDeliveryData = deliveryGroup ? data[deliveryGroup.id] : {};
  const gstAddress = gstInfo?.address || {};
  const panNo = gstInfo?.pan;
  const gstLine1 = [gstAddress.buildingNo, gstAddress.buildingName, gstAddress.flatNo].filter(Boolean).join(", ");
  const gstLine2 = [gstAddress.street, gstAddress.locality, gstAddress.district].filter(Boolean).join(", ");
  const gstCity = gstAddress.location || "";
  const invoiceData = parseGroupData(rawInvoiceData);
  const deliveryData = parseGroupData(rawDeliveryData);
  const existingData = localStorage.getItem("customerAddressIds");
  const customerId = selectedB2BCustomer?.customerId || uuidv4().replace(/-/g, "").toUpperCase();

  let invoiceAddressId = null;
  let deliveryAddressId = null;
  if (switchToggle) {
    const sameAddressId =
      selectedInvoiceAddress ||
      selectedDeliveryAddress ||
      selectedB2BCustomer?.invoiceAddresses?.[0]?.s_customer_address_id ||
      selectedB2BCustomer?.deliveryAddresses?.[0]?.s_customer_address_id ||
      uuidv4().replace(/-/g, "").toUpperCase();
    invoiceAddressId = sameAddressId;
    deliveryAddressId = sameAddressId;
  } else {
    invoiceAddressId =
      selectedB2BCustomer?.invoiceAddresses?.length === 1
        ? selectedB2BCustomer.invoiceAddresses[0].s_customer_address_id
        : selectedInvoiceAddress || selectedB2BCustomer?.invoiceAddresses?.[0]?.s_customer_address_id || null;
    deliveryAddressId =
      selectedB2BCustomer?.deliveryAddresses?.length === 1
        ? selectedB2BCustomer.deliveryAddresses[0].s_customer_address_id
        : selectedDeliveryAddress || selectedB2BCustomer?.deliveryAddresses?.[0]?.s_customer_address_id || null;
    if (!invoiceAddressId) invoiceAddressId = uuidv4().replace(/-/g, "").toUpperCase();
    if (!deliveryAddressId) deliveryAddressId = uuidv4().replace(/-/g, "").toUpperCase();
  }

  let statesData = [];
  let countriesData = [];
  try {
    statesData = JSON.parse(localStorage.getItem("statesData") || "[]");
  } catch (e) {
    statesData = [];
  }
  try {
    countriesData = JSON.parse(localStorage.getItem("countriesData") || "[]");
  } catch (e) {
    countriesData = [];
  }
  if (!statesData.length) statesData = useReferenceStore.getState().statesData || [];
  if (!countriesData.length) countriesData = useReferenceStore.getState().countriesData || [];
  const defaultCountryId = tillData?.tillAccess?.csBunit?.customerAddress?.csCountry?.csCountryID || null;
  const defaultRegionId = tillData?.tillAccess?.csBunit?.customerAddress?.csRegion?.csRegionID || null;
  const findCountryId = (name) => countriesData.find((c) => c.Name === name)?.RecordID || defaultCountryId;
  const findStateId = (name) => statesData.find((s) => s.Name === name)?.RecordID || defaultRegionId;
  const getRegionId = (stateValue, statesData, fallbackRegionId) => {
    if (!stateValue) return fallbackRegionId;

    // 1️⃣ Check if it's already an ID (direct match)
    const byId = statesData.find((state) => state.RecordID === stateValue);
    if (byId) return byId.RecordID;

    // 2️⃣ Check if it's a Name (case insensitive safer)
    const byName = statesData.find((state) => state.Name?.toLowerCase() === stateValue?.toLowerCase());
    if (byName) return byName.RecordID;

    // 3️⃣ Fallback
    return fallbackRegionId;
  };
  const customerPayload = [
    {
      sCustomerId: existingData ? JSON.parse(existingData).customerId : customerId,
      name: data.companyName || "",
      csBunitId: tillData?.tillAccess?.csBunit?.csBunitId || null,
      sCustomerCategoryId: null,
      gstNo: data.gstNo || "",
      linkB2bCustomer: isPanCustomer && isB2BEnabled ? "Y" : "N",
      legalName: data.legalName || "",
      customerAddress: [
        {
          isInvoiced: "Y",
          address_id: invoiceAddressId,
          line1: invoiceData.streetAddress || invoiceData.line1 || gstLine1 || "",
          line2: invoiceData.streetAddress || invoiceData.line2 || gstLine2 || "",
          city: invoiceData.city || gstCity || "",
          postalcode: invoiceData.pincode || gstAddress.pincode || "",
          csCountryId: getCountryId(invoiceData.country, countriesData, tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID),
          phone: data.mobileno || "",
          csRegionId: getRegionId(invoiceData.state, statesData, tillData.tillAccess.csBunit.customerAddress?.csRegion?.csRegionID) || null,
        },
        {
          isInvoiced: "N",
          address_id: deliveryAddressId,
          line1: deliveryData.streetAddress || deliveryData.line1 || (switchToggle ? gstLine1 : "") || "",
          line2: deliveryData.streetAddress || deliveryData.line2 || (switchToggle ? gstLine2 : "") || "",
          city: deliveryData.city || gstCity || "",
          postalcode: deliveryData.pincode || gstAddress.pincode || "",
          csCountryId: getCountryId(deliveryData.country, countriesData, tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID),
          phone: data.mobileno || "",
          csRegionId: getRegionId(deliveryData.state, statesData, tillData.tillAccess.csBunit.customerAddress?.csRegion?.csRegionID) || null,
        },
      ],
      finLinCustomer: [
        {
          gstType: invoiceData.gstType || "",
          gstNo: invoiceData.gstNo || data.gstNo || "",
          pan: invoiceData.pan || panNo || "",
          companyType: invoiceData.companyType || "",
          addressId: invoiceAddressId,
        },
        {
          gstType: deliveryData.gstType || "",
          gstNo: deliveryData.gstNo || data.gstNo || "",
          pan: deliveryData.pan || panNo || "",
          companyType: deliveryData.companyType || "",
          addressId: deliveryAddressId,
        },
      ],
    },
  ];
  return {
    customerPayload,
    customerId,
    invoiceAddressId,
    deliveryAddressId,
  };
};

const buildGraphQLJSONString = (obj) => {
  return JSON.stringify(obj).replace(/"/g, '\\"');
};

const extractGroupedMetaData = (metaData) => {
  if (!Array.isArray(metaData)) return {};

  const { retailCustomerGroups } = getFreshConfig();
  const grouped = {};

  metaData.forEach((item) => {
    const { key, value } = item;

    // only group keys
    if (/^group_\d+$/.test(key)) {
      const group = retailCustomerGroups.find((g) => g.id === key);

      if (!group) return;

      let parsedValue = {};

      try {
        parsedValue = typeof value === "string" ? JSON.parse(value) : value;
      } catch (e) {
        console.error("Invalid JSON in group:", value);
      }

      if (Object.keys(parsedValue).length > 0) {
        grouped[key.toLowerCase()] = parsedValue;
      }
    }
  });

  return grouped;
};

const getAddressFromContactGroup = (data, retailCustomerGroups, retailCustomerTabs) => {
  const contactTabIds = retailCustomerTabs.filter((tab) => tab.name?.toLowerCase().includes("contact")).map((tab) => tab.id);

  const contactAddressGroups = retailCustomerGroups.filter((group) => contactTabIds.includes(group.tabId) && group.name?.toLowerCase().includes("address"));

  for (const group of contactAddressGroups) {
    const rawGroupData = data[group.id];
    const groupData = parseGroupData(rawGroupData);
    if (groupData && Object.keys(groupData).length > 0) {
      return {
        line1: groupData.line1 || null,
        line2: groupData.line2 || null,
        city: groupData.city || null,
        state: groupData.state || null,
      };
    }
  }

  return {};
};

export const createCustomer = async (
  data,
  cart,
  setCart,
  form,
  tillLayout,
  closeCustomerSearch,
  tillData,
  posLogActivity,
  setKioskUI,
  setDisplayAddNewCustomer,
  UAECustomerForm,
  customerForm,
  setTillLink,
  setCustomerLoading,
  setDisplayUAECustomer,
  isCallCenterFlow,
  setCallCenterVisible,
  setIsCallCenterFlow,
  history,
  setIsSubmittingCustomer,
  setLastSearchedValue,
  setSelectedB2BCustomer,
  selectedB2BCustomer,
  setSwitchToggle,
  setFormDataCache,
  isPanCustomer,
  setIsPanCustomer,
  isB2BEnabled,
  setPanCustomerInfo,
  panCustomerInfo,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit,
  isRefundWalletOpen,
  setIsRefundWalletOpen,
  isNriBilling,
  setIsNriBilling,
  isCreditSale,
  setIsCreditSale,
  panDrawerMode,
  documents,
  setGstInfo,
  retailCustomerFields,
  gstInfo,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  setSelectedInvoiceAddress,
  setSelectedDeliveryAddress,
  switchToggle
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  let setAuthTokens;
  const t = i18next.t;
  const authHeaders = await getOAuthHeaders();
  if (authHeaders && authHeaders.access_token) {
    setAuthTokens = authHeaders.access_token;
  }
  if (data["Tax Id"] && !data.taxId) data.taxId = data["Tax Id"];
  if (data.taxid && !data.taxId) data.taxId = data.taxid;
  if (data.taxId && !data.taxID) data.taxID = data.taxId;

  const { retailCustomerGroups, retailCustomerFields: freshRetailFields, retailCustomerTabs, businessTabIds, businessGroupIds } = getFreshConfig();
  const activeRetailFields = retailCustomerFields || freshRetailFields;
  const ungroupedBusinessFields = activeRetailFields.filter((field) => businessTabIds.includes(field.tabId) && (!field.groupId || !businessGroupIds.includes(field.groupId)));
  const ungroupedFieldsObj = ungroupedBusinessFields.reduce((acc, field) => {
    const camelCaseKey = toCamelCase(field.name);
    const value = data[camelCaseKey];
    if (value !== "" && value !== null && value !== undefined) {
      acc[field.name?.trim() || field.id] = String(value);
    }
    return acc;
  }, {});
  const groupedCustomerListObj = Object.entries(data)
    .filter(([key]) => businessGroupIds.includes(String(key)))
    .reduce((acc, [groupId, groupData]) => {
      const group = retailCustomerGroups.find((g) => g.id === groupId);
      const groupName = group?.name?.trim() || "";

      if (typeof groupData === "object" && groupData !== null) {
        const cleanedFields = Object.entries(groupData).reduce((fieldAcc, [fieldKey, fieldValue]) => {
          if (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined) {
            fieldAcc[fieldKey.trim()] = String(fieldValue);
          }
          return fieldAcc;
        }, {});

        if (Object.keys(cleanedFields).length > 0) {
          acc[groupName] = cleanedFields;
        }
      }
      return acc;
    }, {});
  let customerListObj = {};
  if (isPanCustomer) {
    customerListObj = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  } else {
    customerListObj = {
      ...ungroupedFieldsObj,
      ...groupedCustomerListObj,
    };
  }

  // const stringifiedData = JSON.stringify(customerListObj);
  // const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  // const newStringRequest = JSON.stringify(newStringifiedFields);

  // Only build customer list payload when both PAN and B2B are enabled
  let customerPayload = [];
  let customerId = null;
  let billingAddressId = null;
  let shippingAddressId = null;
  if (isB2BEnabled) {
    const payloadData = buildCustomerListPayload(
      data,
      selectedB2BCustomer,
      panCustomerInfo,
      tillData,
      gstInfo,
      isPanCustomer,
      isB2BEnabled,
      selectedInvoiceAddress,
      selectedDeliveryAddress,
      switchToggle
    );
    customerPayload = payloadData.customerPayload;
    customerId = payloadData.customerId;
    billingAddressId = payloadData.invoiceAddressId;
    shippingAddressId = payloadData.deliveryAddressId;
    const existingData = localStorage.getItem("customerAddressIds");
    if (!existingData) {
      localStorage.setItem("customerAddressIds", JSON.stringify({ customerId: customerId, billingId: billingAddressId, shippingId: shippingAddressId }));
    }
  }
  const newStringRequest = buildGraphQLJSONString(customerPayload);

  if (isB2BEnabled) {
    useTransactionStore.getState().setIsB2bEnabled(String(isB2BEnabled));
  } else {
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsB2bEnabled(null);
  }

  if (isCreditSale) {
    useTransactionStore.getState().setIsCreditSale(String(isCreditSale));
  }

  if (isPanCustomer) {
    localStorage.setItem("isPanCustomer", String(isPanCustomer));
  }

  if (isNriBilling) {
    localStorage.setItem("isNriBilling", String(isNriBilling));
  }

  function toNormalCase(key) {
    return key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  }
  const EXCLUDED_META_KEYS = new Set(["customerdocumenturls", "pancardurl", "customer_document_urls", "pan_card_url", "passporturl", "passport_url", "scustomer"]);
  const usedKeys = getUsedKeys(retailCustomerGroups, retailCustomerTabs);
  const ungroupedBusinessKeys = ungroupedBusinessFields.map((field) => toCamelCase(field.name));
  const businessFieldKeysSet = new Set(ungroupedBusinessKeys.map((k) => k.toLowerCase()));
  const metaData = Object.entries(data)
    .filter(([key, value]) => {
      const normalizedKey = key.trim().toLowerCase().replace(/[_\s]/g, "");
      const lowerKey = key.toLowerCase();
      // grouped business fields
      const isGroupedBusinessField = businessGroupIds.includes(String(key));
      // ungrouped business fields
      const isUngroupedBusinessField = businessFieldKeysSet.has(lowerKey);
      const isBusinessField = isGroupedBusinessField || isUngroupedBusinessField;
      // MAIN CONDITION
      // if (isBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }
      // if (isUngroupedBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }

      return !usedKeys.has(key.trim().toLowerCase()) && !EXCLUDED_META_KEYS.has(normalizedKey) && value != null && value !== "";
    })
    .map(([key, value]) => ({
      key: /^group_\d+$/i.test(key) ? key.toLowerCase() : toNormalCase(key),
      value: typeof value === "object" ? JSON.stringify(value) : String(value),
    }));
  // ADD CUSTOMER DOCUMENT URLS
  if (documents && documents.length > 0) {
    const documentUrls = documents.filter((doc) => doc?.docUrl && doc.docName === "Customer Document").map((doc) => doc.docUrl);

    if (documentUrls.length > 0) {
      metaData.push({
        key: "CUSTOMER_DOCUMENT_URLS",
        value: JSON.stringify(documentUrls),
      });
    }
  }

  // PAN URL SHOULD BE ADDED SEPARATELY
  if (documents && documents.length > 0) {
    const panDoc = documents.find((doc) => doc.docName === "PAN Card");

    if (panDoc?.docUrl) {
      metaData.push({
        key: "PAN_CARD_URL",
        value: panDoc.docUrl,
      });
    }
  }
  if (documents && documents.length > 0) {
    const passportDoc = documents.find((doc) => doc.docName === "Passport");
    if (passportDoc?.docUrl) {
      metaData.push({
        key: "PASSPORT_URL",
        value: passportDoc.docUrl,
      });
    }
  }
  const loyaltyLevel =
    retailCustomerFields && retailCustomerFields.length > 0
      ? data.loyaltyLevel || data.program
      : data.program && data.program !== "" && data.loyality === true
        ? data.program
        : null;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const serverUrl = process.env.REACT_APP_serverUrl;
  const newCustomerId = uuidv4().replace(/-/g, "").toUpperCase();

  const mobileNo = data.mobile || data.mobileno ? data.mobile || data.mobileno : null;
  if (mobileNo) {
    const paramsInput = {
      query: `query{
          customerSearch(searchKey:"${mobileNo}",bunitId:"${tillData?.tillAccess?.csBunit?.csBunitId}"){
            cwrCustomerId
            sCustomerAddress
            localisation
            sPriceListId 
            code   
            name
            email        
            mobileNo
            sCustomerAddress
            cardNumber
            regType
            validity
            name
            creditLimit
            creditBalance
            creditApproachPercentage
            pincode
            retlLoyaltyBalance
            b2cRegisteredstoreId
             iscredit
             lastVisitDate
             noOfVisits
             lastBillingAmount
             averageBasketValue
             loyaltyPointToExpire
             firstName
              lastName
               gender
                taxId
            anniversaryDate
             birthday
            loyaltyLevel{
                cwrLoyaltyLevelId
                name
                accumulationRate
                redemptionRate
            }
            cwrWallet {
                 wallet_Id
                balance_Amt
            }
            sCustomer{
              sCustomerID
              customerCategory{
                sCustomerCateforyId
                value
                name
                description
              }
            }
            customerAddress {
                line1 
                line2 
                city 
                region
                 country 
                 csCountryId
                  csRegionId
            }
            csBunit{
              csBunitId
              name
            }
            b2cCustomerSegment{
              name
              cwrB2CCustomerSegmentId
            }
            sPriceListId
            priceListName
            metaData {
              key
              value
            }
          }
        }`,
    };
    try {
      const response = await getCustomerService(paramsInput);
      const customerSearchResults = response?.data?.data?.customerSearch;
      if (customerSearchResults.length > 0) {
        message.warning(`Customer already exists with mobile number ${mobileNo}`);

        setCustomerLoading(false);
        return;
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Customer search failed:", error);
      message.error("Unable to validate mobile number.");
      return;
    }
  }

  const contactAddress = getAddressFromContactGroup(data, retailCustomerGroups, retailCustomerTabs);
  const newRequest = {
    query: `mutation {
        upsertB2CCustomer(
          id: "${newCustomerId}"
          type: "New"
          customer: {
            csBunitId: "${tillData.tillAccess.csBunit.csBunitId}",
            csClientId: "${tillData.tillAccess.csClientId}",
            createdby: "${tillData.tillAccess.csUserId}",
            updatedby: "${tillData.tillAccess.csUserId}",
            code: ${data.mobile ? `"${data.mobile.toLowerCase()}"` : null},
            name: ${data.name || data.customerName ? `"${data.name || data.customerName}"` : null},
            mobileNo: ${data.mobile || data.mobileno ? `"${data.mobile || data.mobileno}"` : null},
            email: ${data.email ? `"${data.email}"` : null},
            pincode: ${data.pincode ? data.pincode : null},
            sCustomerId: ${isB2BEnabled && selectedB2BCustomer?.customerId ? `"${selectedB2BCustomer.customerId}"` : null},
            finPaymentmethodId: null,
            retlLoyaltyBalance: null,
            iscredit: false,
            b2cRegisteredstoreId: "${tillData.tillAccess.csBunit.csBunitId}",
            firstName: ${data.firstName ? `"${data.firstName}"` : null},
            lastName: ${data.lastName ? `"${data.lastName}"` : null},
            gender: ${data.gender !== "" && data.gender !== undefined ? `"${data.gender}"` : null},
            taxId: ${data.taxID ? `"${data.taxID}"` : null},
            anniversaryDate: ${
              data.anniversaryDate !== undefined && data.anniversaryDate !== null && data.anniversaryDate !== "" ? `"${dayjs(data.anniversaryDate).format("YYYY-MM-DD")}"` : null
            },
            birthday: ${
              data.BirthdayDate || data.birthday
                ? dayjs(data.BirthdayDate || data.birthday).isValid()
                  ? `"${dayjs(data.BirthdayDate || data.birthday).format("YYYY-MM-DD")}"`
                  : null
                : null
            },
            lastVisitDate: ${data.lastVisitDate !== undefined && data.lastVisitDate !== null ? `"${dayjs(data.lastVisitDate).format("YYYY-MM-DD")}"` : null},
            noOfVisits: 0,
            validity: ${dayjs(data.validity).isValid() ? `"${dayjs(data.validity).format("YYYY-MM-DD")}"` : null},
            regType: ${data.type ? `"${data.type}"` : null},
            cardNumber: ${data.cardNumber ? `"${data.cardNumber}"` : null},
            lastBillingAmount: 0,
            averageBasketValue: 0,
            loyaltyPointToExpire: null,
            metaData: ${JSON.stringify(metaData)
              .replace(/"key":/g, "key:")
              .replace(/"value":/g, "value:")
              .replace(/"/g, '"')},
            loyaltyLevel: { cwrLoyaltyLevelId:  ${loyaltyLevel && loyaltyLevel !== null && loyaltyLevel !== "undefined" ? `"${loyaltyLevel}"` : null} }
            customerAddress: {
              line1: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line1 ? `"${contactAddress.line1}"` : data.street ? `"${data.street}"` : null) : data.street ? `"${data.street}"` : null},
              line2: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line2 ? `"${contactAddress.line2}"` : null) : data.city ? `"${data.city}"` : null},
              city: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.city ? `"${contactAddress.city}"` : data.city ? `"${data.city}"` : null) : data.city ? `"${data.city}"` : null},
              csRegionId: ${
                retailCustomerFields && retailCustomerFields.length > 0
                  ? contactAddress?.state
                    ? `"${contactAddress.state}"`
                    : `"${tillData.tillAccess.csBunit.customerAddress.csRegion.csRegionID}"`
                  : tillData.tillAccess.csBunit.customerAddress?.csRegion?.csRegionID
                    ? `"${tillData.tillAccess.csBunit.customerAddress.csRegion.csRegionID}"`
                    : null
              },
              csCountryId: ${tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID ? `"${tillData.tillAccess.csBunit.customerAddress.csCountry.csCountryID}"` : null},
            }
            b2bCustomer: "${isPanCustomer ? "Y" : isB2BEnabled ? "Y" : "N"}",
            customerList: "${newStringRequest}"
          }
        ) {
          cwrCustomerId
          sPriceListId
          code
          name
          email
          mobileNo
          pincode
          retlLoyaltyBalance
          b2cRegisteredstoreId
          lastVisitDate
          noOfVisits
          lastBillingAmount
          averageBasketValue
          loyaltyPointToExpire
          firstName
          lastName
          gender
          taxId
          cardNumber
          regType
          validity
          anniversaryDate
          birthday
          sCustomer {
            sCustomerID
            customerCategory {
              sCustomerCateforyId
              value
              name
              description
            }
          }
          csBunit {
            csBunitId
            name
          }
          loyaltyLevel {
            cwrLoyaltyLevelId
          }
          customerAddress {
            line1
            line2
            city
            region
            country
            csCountryId
            csRegionId
          }
          status
          message
          metaData {
            key
            value
          }
        }
      }`,
  };

  try {
    setCustomerLoading(true);
    const response = await createCustomerService(newRequest);

    const result = response?.data?.data?.upsertB2CCustomer;

    if (result?.status === "200") {
      if (tillLayout === 3) {
        localStorage.setItem("kioskUI", 3);
        setKioskUI(3);
        const customEvent = new CustomEvent("customStorageChange", {
          detail: { key: "kioskUI", newValue: 3 },
        });
        window.dispatchEvent(customEvent);
      }

      form.resetFields();
      setSwitchToggle(false);
      customerForm.resetFields();
      UAECustomerForm.resetFields();
      setDisplayAddNewCustomer(false);
      setDisplayUAECustomer(false);
      setTillLink(false);
      setFormDataCache({});
      if (panDrawerMode !== "VALIDATION") {
        setPanCustomerInfo(null);
        setIsPanCustomer(false);
      }
      setIsSubmittingCustomer(false);
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      setSelectedB2BCustomer(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
      const selectedPriceListId = result?.sPriceListId ?? saleTypeData?.customer?.sPriceListId;
      const cartCustomer = {
        ...result,
        sPriceListId: selectedPriceListId,
        newB2bCustomerId: customerId,
      };
      if (useTransactionStore.getState().refundCustomerEdit === "Y" && cart?.isReturn === true) {
        useTransactionStore.getState().setRefundReturnCustomerId(result);

        useTransactionStore.getState().setRefundCustomerEdit(null);
        setIsRefundWalletOpen(true); // reopen wallet drawer
        closeCustomerSearch();
        return;
      }

      const updatedMetaData = result?.metaData || [];

      const groupedMetaData = isB2BEnabled ? extractGroupedMetaData(updatedMetaData) : undefined;

      // Extract customerProperties from response metaData (same pattern as updateCustomer).
      // data?.customerProperties may be undefined if the drawer footer didn't attach them,
      // so fall back to parsing the "Customer Properties" metaData entry from the server.
      let createdCustomerProperties = data?.customerProperties;
      if (Array.isArray(updatedMetaData)) {
        const cpEntry = updatedMetaData.find((m) => m.key === "Customer Properties");
        if (cpEntry?.value) {
          try {
            const parsed = typeof cpEntry.value === "string" ? JSON.parse(cpEntry.value) : cpEntry.value;
            if (parsed && typeof parsed === "object") {
              createdCustomerProperties = { ...(createdCustomerProperties || {}), ...parsed };
            }
          } catch (e) {
            /* keep existing */
          }
        }
      }
      const customerAddressIds =
        window.location.pathname.includes("/estimate") && localStorage.getItem("customerAddressIds") ? JSON.parse(localStorage.getItem("customerAddressIds")) : null;
      const updatedCart = {
        ...cart,
        customer: {
          ...result,
          customerProperties: createdCustomerProperties,
          metaData: updatedMetaData,
          ...(groupedMetaData && { groupedMetaData }),
          sPriceListId: selectedPriceListId,
          newB2bCustomerId: customerId,
          nriBilling: isNriBilling,
          ...(window.location.pathname.includes("/estimate") && localStorage.getItem("isB2bEnabled") === "true" ? { isB2bEnabled: "Y" } : {}),
          ...(customerAddressIds ? { customerAddressIds } : {}),
        },
      };
      if (updatedCart.items?.length > 0) {
        const precision = useAuthStore.getState().tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
        const isInterState = getIsInterState(updatedCart);
        updatedCart.groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState });
      }
      if (isExchange) {
        useCartStore.getState().setExchangeCart(updatedCart);
      } else {
        setCart(updatedCart);
      }

      // Show success message BEFORE closing drawer so user sees feedback
      message.success(result.message || "Customer created successfully");
      closeCustomerSearch();
      setIsNriBilling(false);
      setIsCreditSale(false);
      if (isCallCenterFlow && tillLayout === 2) {
        history.push("/call-center");
        setCallCenterVisible(true);
      }
      const logConfig = await db.logConfiguration.toArray();
      const logCustomerAdd = logConfig.some((config) => config.addCustomer.some((item) => item.log === "Y"));

      if (logCustomerAdd) {
        posLogActivity(result.name, "ACT");
      }
      setCustomerLoading(false);
    } else {
      message.error(t("customer_edit_error"));
      setCustomerLoading(false);
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      setPanCustomerInfo(null);
    }
  } catch (error) {
    Sentry.captureException(new Error("Create customer failed"), {
      extra: newRequest,
    });
    message.error(t("Failed to create new customer. Please check the entered data and try again."));
    setCustomerLoading(false);
    setLastSearchedValue("");
    if (typeof setGstInfo === "function") setGstInfo(null);
    if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
    if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
    setPanCustomerInfo(null);
  }
};

export const updateCustomer = async (
  data,
  cart,
  setCart,
  form,
  closeCustomerSearch,
  selectedEditOldCustomer,
  setSelectedEditOldCustomer,
  setDisplayEditOldCustomer,
  tillData,
  customer,
  UAECustomerForm,
  setDisplayAddNewCustomer,
  setFormDataCache,
  setIsSubmittingCustomer,
  setLastSearchedValue,
  setSelectedB2BCustomer,
  selectedB2BCustomer,
  isCallCenterFlow,
  setCallCenterVisible,
  setIsCallCenterFlow,
  history,
  tillLayout,
  isPanCustomer,
  setIsPanCustomer,
  isB2BEnabled,
  setPanCustomerInfo,
  panCustomerInfo,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit,
  isRefundWalletOpen,
  setIsRefundWalletOpen,
  isNriBilling,
  setIsNriBilling,
  isCreditSale,
  setIsCreditSale,
  panDrawerMode,
  documents,
  setGstInfo,
  gstInfo,
  setExchangeCartTcs,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  setSelectedInvoiceAddress,
  setSelectedDeliveryAddress,
  switchToggle,
  setSwitchToggle
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  const activeCustomer = isExchange ? useCartStore.getState().exchangeCart?.customer || selectedEditOldCustomer || cart?.customer : cart?.customer;
  const { retailCustomerGroups, retailCustomerFields, retailCustomerTabs, businessTabIds, businessGroupIds } = getFreshConfig();
  const t = i18next.t;
  let setAuthTokens;
  const authHeaders = await getOAuthHeaders();
  if (data && typeof data === "object") {
    if (data["Tax Id"] && !data.taxId) data.taxId = data["Tax Id"];
    if (data.taxid && !data.taxId) data.taxId = data.taxid;
    if (data.taxID && !data.taxId) data.taxId = data.taxID;
    // create consistent uppercase variant if server sometimes expects that
    if (data.taxId && !data.taxID) data.taxID = data.taxId;
  }

  if (authHeaders && authHeaders.access_token) {
    setAuthTokens = authHeaders.access_token;
  }

  function toNormalCase(key) {
    return key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  }

  const ungroupedBusinessFields = retailCustomerFields.filter((field) => businessTabIds.includes(field.tabId) && (!field.groupId || !businessGroupIds.includes(field.groupId)));
  const EXCLUDED_META_KEYS = new Set([
    "customerdocumenturls",
    "pancardurl",
    "customer_document_urls",
    "pan_card_url",
    "passporturl",
    "passport_url",
    "sCustomer",
    "cwrcustomerid",
    "spricelistid",
    "csbunit",
    "customeraddress",
    "cwrwallet",
    "scustomer",
    "b2ccustomersegment",
    "creditapproachpercentage",
    "creditbalance",
    "creditlimit",
    "iscredit",
    "retlloyaltybalance",
    "b2cregisteredstoreid",
    "code",
    "lastbillingamount",
    "noofvisits",
    "averagebasketvalue",
    "pricelistname",
    "metadata",
    "nriBilling",
  ]);
  const usedKeys = getUsedKeys(retailCustomerGroups, retailCustomerTabs);
  const ungroupedBusinessKeys = ungroupedBusinessFields.map((field) => toCamelCase(field.name));
  const businessFieldKeysSet = new Set(ungroupedBusinessKeys.map((k) => k.toLowerCase()));
  const metaData = Object.entries(data)
    .filter(([key, value]) => {
      const normalizedKey = key.trim().toLowerCase().replace(/[_\s]/g, "");
      const lowerKey = key.toLowerCase();
      // grouped business fields
      const isGroupedBusinessField = businessGroupIds.includes(String(key));
      // ungrouped business fields
      const isUngroupedBusinessField = businessFieldKeysSet.has(lowerKey);
      const isBusinessField = isGroupedBusinessField || isUngroupedBusinessField;
      // MAIN CONDITION
      // if (isBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }
      // if (isUngroupedBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }
      return !usedKeys.has(key.trim().toLowerCase()) && !EXCLUDED_META_KEYS.has(normalizedKey) && value != null && value !== "";
    })
    .map(([key, value]) => {
      let normalizedKey = key.trim();
      if (/^group_\d+$/i.test(normalizedKey)) {
        normalizedKey = normalizedKey.toLowerCase();
      } else {
        normalizedKey = toNormalCase(key);
      }
      return {
        key: normalizedKey,
        value: typeof value === "object" ? JSON.stringify(value) : String(value),
      };
    });
  // Merge existing + newly uploaded documents
  // BUILD CUSTOMER DOCUMENT URLS (merge existing + new)
  const existingDocUrls = (() => {
    const meta = activeCustomer?.metaData || selectedEditOldCustomer?.metaData || [];
    const docMeta = meta.find((m) => m.key === "CUSTOMER_DOCUMENT_URLS");
    if (!docMeta?.value) return [];
    try {
      return JSON.parse(docMeta.value);
    } catch {
      return [];
    }
  })();

  const newDocUrls = documents.filter((doc) => doc?.docUrl && doc.docName === "Customer Document").map((doc) => doc.docUrl);

  // Merge: existing first, then new ones (deduplicated)
  const allDocUrls = [...new Set([...existingDocUrls, ...newDocUrls])];

  if (allDocUrls.length > 0) {
    metaData.push({
      key: "CUSTOMER_DOCUMENT_URLS",
      value: JSON.stringify(allDocUrls),
    });
  }
  // PAN CARD URL (preserve existing if no new upload)
  const newPanDoc = documents.find((doc) => doc.docName === "PAN Card");

  if (newPanDoc?.docUrl) {
    // User uploaded a new PAN card
    metaData.push({
      key: "PAN_CARD_URL",
      value: newPanDoc.docUrl,
    });
  } else {
    // Preserve existing PAN card URL from cart/customer
    const meta = activeCustomer?.metaData || selectedEditOldCustomer?.metaData || [];
    const existingPan = meta.find((m) => m.key === "PAN_CARD_URL");
    if (existingPan?.value) {
      metaData.push({
        key: "PAN_CARD_URL",
        value: existingPan.value,
      });
    }
  }

  const newPassportDoc = documents.find((doc) => doc.docName === "Passport");

  if (newPassportDoc?.docUrl) {
    metaData.push({
      key: "PASSPORT_URL",
      value: newPassportDoc.docUrl,
    });
  } else {
    // Preserve existing passport URL
    const meta = activeCustomer?.metaData || selectedEditOldCustomer?.metaData || [];
    const existingPassport = meta.find((m) => m.key === "PASSPORT_URL");
    if (existingPassport?.value) {
      metaData.push({
        key: "PASSPORT_URL",
        value: existingPassport.value,
      });
    }
  }

  const ungroupedFieldsObj = ungroupedBusinessFields.reduce((acc, field) => {
    const camelCaseKey = toCamelCase(field.name);
    const value = data[camelCaseKey];
    if (value !== "" && value !== null && value !== undefined) {
      acc[field.name?.trim() || field.id] = String(value);
    }
    return acc;
  }, {});

  const groupedCustomerListObj = Object.entries(data)
    .filter(([key]) => businessGroupIds.includes(String(key)))
    .reduce((acc, [groupId, groupData]) => {
      const group = retailCustomerGroups.find((g) => g.id === groupId);
      const groupName = group?.name?.trim() || "";

      if (typeof groupData === "object" && groupData !== null) {
        const cleanedFields = Object.entries(groupData).reduce((fieldAcc, [fieldKey, fieldValue]) => {
          if (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined) {
            fieldAcc[fieldKey.trim()] = String(fieldValue);
          }
          return fieldAcc;
        }, {});

        if (Object.keys(cleanedFields).length > 0) {
          acc[groupName] = cleanedFields;
        }
      }
      return acc;
    }, {});

  let customerListObj = {};

  if (isPanCustomer) {
    customerListObj = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  } else {
    customerListObj = {
      ...ungroupedFieldsObj,
      ...groupedCustomerListObj,
    };
  }

  // const stringifiedData = JSON.stringify(customerListObj);
  // const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  // const newStringRequest = JSON.stringify(newStringifiedFields);

  let customerListPayload = null;
  let invoiceAddressId = null;
  let deliveryAddressId = null;
  let newB2bCustomerId = null;
  if (isB2BEnabled) {
    const payloadData = buildCustomerListPayload(
      data,
      selectedB2BCustomer,
      panCustomerInfo,
      tillData,
      gstInfo,
      isPanCustomer,
      isB2BEnabled,
      selectedInvoiceAddress,
      selectedDeliveryAddress,
      switchToggle
    );
    customerListPayload = payloadData.customerPayload;
    invoiceAddressId = payloadData.invoiceAddressId;
    deliveryAddressId = payloadData.deliveryAddressId;
    newB2bCustomerId = payloadData.customerId;
  }
  const newStringRequest = buildGraphQLJSONString(customerListPayload);

  if (isB2BEnabled) {
    localStorage.setItem("isB2bEnabled", "true");
    useTransactionStore.getState().setIsB2bEnabled(String(isB2BEnabled));
  } else {
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsB2bEnabled(null);
  }

  if (isCreditSale) {
    useTransactionStore.getState().setIsCreditSale(String(isCreditSale));
  }

  if (isPanCustomer) {
    localStorage.setItem("isPanCustomer", String(isPanCustomer));
  }

  if (isNriBilling) {
    localStorage.setItem("isNriBilling", String(isNriBilling));
  }

  const posConfig = usePOSConfigStore.getState().posConfig;
  const serverUrl = process.env.REACT_APP_serverUrl;
  let customerId = selectedEditOldCustomer?.cwrCustomerId || (activeCustomer?.b2cRegisteredstoreId !== null ? activeCustomer?.cwrCustomerId : null);
  let resolvedSCustomerId = null;
  if (isB2BEnabled) {
    const newsCustomerId = selectedB2BCustomer ? selectedB2BCustomer?.customerId : activeCustomer?.sCustomer?.sCustomerID;
    resolvedSCustomerId = newsCustomerId || null;
  }
  // const loyaltyLevel =
  // retailCustomerFields && retailCustomerFields.length > 0 ? data.loyaltyLevel : data.program && data.program !== "" && data.loyality === true ? data.program : null;
  const loyaltyLevel =
    retailCustomerFields && retailCustomerFields.length > 0
      ? data.loyaltyLevel?.cwrLoyaltyLevelId
      : data.program && data.program !== "" && data.loyality === true
        ? data.program
        : null;

  const oldMobile = selectedEditOldCustomer?.mobileNo?.trim();
  const newMobile = (data.mobileno || data.editMobile || "").trim();

  const isMobileChanged = newMobile && newMobile !== oldMobile;

  if (isMobileChanged) {
    const paramsInput = {
      query: `query{
      customerSearch(
        searchKey:"${newMobile}",
        bunitId:"${tillData?.tillAccess?.csBunit?.csBunitId}"
      ){
        cwrCustomerId
        name
        mobileNo
      }
    }`,
    };

    try {
      const response = await getCustomerService(paramsInput);
      const searchResults = response?.data?.data?.customerSearch || [];

      // ✅ If found AND not same customer
      const duplicateCustomer = searchResults.find((cust) => cust.cwrCustomerId !== selectedEditOldCustomer?.cwrCustomerId);

      if (duplicateCustomer) {
        message.warning(`Mobile number already registered with customer "${duplicateCustomer.name}"`);
        return; // ⛔ STOP update
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error("Unable to validate mobile number.");
      return; // ⛔ Stop update
    }
  }
  const contactAddress = getAddressFromContactGroup(data, retailCustomerGroups, retailCustomerTabs);

  // Extract regionId from group_NN values (32-char hex IDs) or fallback to existing customer's customerAddress.csRegionId
  const getStateIdFromGroups = (data) => {
    for (const [key, value] of Object.entries(data)) {
      if (/^group_\d+$/.test(key)) {
        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : value;
          if (parsed?.state && parsed.state.length === 32 && /^[A-F0-9]+$/i.test(parsed.state)) {
            return parsed.state;
          }
        } catch (err) {
          console.error("[Customer] Failed to parse state metadata:", err);
        }
      }
    }
    return null;
  };
  const getRegionFromMetadata = () => {
    const meta = selectedEditOldCustomer?.metaData || activeCustomer?.metaData || [];
    const stateMeta = meta.find((m) => m.key === "state" || m.key === "csRegionId");
    if (stateMeta?.value && stateMeta.value.length === 32 && /^[A-F0-9]+$/i.test(stateMeta.value)) {
      return stateMeta.value;
    }
    return selectedEditOldCustomer?.customerAddress?.csRegionId || activeCustomer?.customerAddress?.csRegionId || null;
  };
  const selectedStateId =
    getStateIdFromGroups(data) || data?.customerAddress?.csRegionId || getRegionFromMetadata() || tillData?.tillAccess?.csBunit?.customerAddress?.csRegion?.csRegionID || null;

  const isExistingCustomer = !!customerId;
  const newRequest = {
    query: `mutation{
              upsertB2CCustomer(id: "${customerId || ""}",
                type:"${isExistingCustomer ? "Old" : "New"}",
                customer: {
                  csBunitId: "${tillData.tillAccess.csBunit.csBunitId}",
                  createdby: "${tillData.tillAccess.csUserId}",
                  csClientId: "${tillData.tillAccess.csClientId}",
                  updatedby: "${tillData.tillAccess.csUserId}",
                  code: null
                 ${(() => {
                   const resolvedName = data?.name || data?.customerName || data?.editName || selectedEditOldCustomer?.name || customer?.name || customer?.firstName || "";
                   const resolvedFirstName = data?.firstName || data?.editName || selectedEditOldCustomer?.firstName || customer?.firstName || resolvedName || "";
                   return `name: ${resolvedName ? `"${resolvedName}"` : null},
                            firstName: ${resolvedFirstName ? `"${resolvedFirstName}"` : null},`;
                 })()}
                  mobileNo: ${data.mobileno || data.editMobile ? `"${data.mobileno || data.editMobile}"` : null},
                  email: ${data.email || data.editEmail ? `"${data.email || data.editEmail}"` : null},
                  pincode: ${data.pincode || data.editPincode ? data.pincode || data.editPincode : null},
                  validity: ${data.validity ? `"${dayjs(data.validity).format("YYYY-MM-DD")}"` : null},
                  regType: ${data.type ? `"${data.type}"` : null},
                  cardNumber: ${data.cardNumber ? `"${data.cardNumber}"` : null},
                  sCustomerId: ${resolvedSCustomerId ? `"${resolvedSCustomerId}"` : null},
                  finPaymentmethodId: null,
                  retlLoyaltyBalance: null,
                  iscredit: false,
                  b2cRegisteredstoreId: "${tillData.tillAccess.csBunit.csBunitId}",
                  lastName: ${data.lastName ? `"${data.lastName}"` : null},
                  gender: ${data.gender && data.gender !== "null" ? `"${data.gender}"` : null},
                  taxId: ${
                    data.taxId || data.taxID || selectedB2BCustomer?.taxId || selectedEditOldCustomer?.taxId
                      ? `"${(data.taxId || data.taxID || selectedB2BCustomer?.taxId || selectedEditOldCustomer?.taxId).replace(/"/g, '\\"')}"`
                      : null
                  },
                  anniversaryDate: ${
                    data.anniversaryDate !== undefined && data.anniversaryDate !== "" && data.anniversaryDate !== null
                      ? `"${dayjs(data.anniversaryDate).format("YYYY-MM-DD")}"`
                      : null
                  },
                  birthday: ${data.BirthdayDate || data.birthday ? `"${dayjs(data.BirthdayDate || data.birthday).format("YYYY-MM-DD")}"` : null},
                  lastVisitDate: ${data.lastVisitDate !== undefined && data.lastVisitDate !== null ? `"${dayjs(data.lastVisitDate).format("YYYY-MM-DD")}"` : null},
                  noOfVisits: 0,
                  lastBillingAmount: 0,
                  averageBasketValue: 0,
                  loyaltyPointToExpire: null,
                  metaData: ${JSON.stringify(metaData)
                    .replace(/"key":/g, "key:")
                    .replace(/"value":/g, "value:")
                    .replace(/"/g, '"')},
                  loyaltyLevel: { cwrLoyaltyLevelId:  ${loyaltyLevel && loyaltyLevel !== null && loyaltyLevel !== "undefined" ? `"${loyaltyLevel}"` : null} }
                  customerAddress: {
                        line1: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line1 ? `"${contactAddress.line1}"` : data.street ? `"${data.street}"` : null) : data.street ? `"${data.street}"` : null},
                        line2: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line2 ? `"${contactAddress.line2}"` : null) : data.city ? `"${data.city}"` : null},
                        city: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.city ? `"${contactAddress.city}"` : data.city ? `"${data.city}"` : null) : data.city ? `"${data.city}"` : null},
                        csRegionId: ${selectedStateId ? `"${selectedStateId}"` : null},
                        csCountryId: ${tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID ? `"${tillData.tillAccess.csBunit.customerAddress.csCountry.csCountryID}"` : null},
                  }
                  b2bCustomer: "${isPanCustomer ? "Y" : isB2BEnabled ? "Y" : "N"}",
                  customerList: "${newStringRequest}"            
                }){
                cwrCustomerId
                sPriceListId
                code
                name
                email
                mobileNo
                pincode
                retlLoyaltyBalance
                b2cRegisteredstoreId
                lastVisitDate
                noOfVisits
                lastBillingAmount
                averageBasketValue
                loyaltyPointToExpire
                firstName
                lastName
                gender
                taxId
                cardNumber
                regType
                validity
                anniversaryDate
                birthday
                sCustomer {
                  sCustomerID
                  customerCategory {
                    sCustomerCateforyId
                    value
                    name
                    description
                  }
                }
                csBunit {
                  csBunitId
                  name
                }
                customerAddress {
                  line1
                  line2
                  city
                  region
                  country
                  csCountryId
                  csRegionId
                }
                loyaltyLevel { 
                  cwrLoyaltyLevelId}
                status
                message
                metaData { 
                  key
                  value
                }
              }
            }`,
  };

  try {
    const response = await updateCustomerService(newRequest);

    const result = response?.data?.data?.upsertB2CCustomer;
    const updatedMetaData = result?.metaData || [];
    const groupedMetaData = isB2BEnabled ? extractGroupedMetaData(updatedMetaData) : null;
    console.debug("result", result);
    if (result?.status === "200") {
      form.resetFields();
      UAECustomerForm.resetFields();
      setSelectedEditOldCustomer();
      setDisplayEditOldCustomer(false);
      setDisplayAddNewCustomer(false);
      setFormDataCache({});
      if (panDrawerMode !== "VALIDATION") {
        setPanCustomerInfo(null);
        setIsPanCustomer(false);
      }
      if (isB2BEnabled) {
        const existingData = localStorage.getItem("customerAddressIds");
        if (!existingData) {
          localStorage.setItem("customerAddressIds", JSON.stringify({ customerId: newB2bCustomerId, billingId: invoiceAddressId, shippingId: deliveryAddressId }));
        }
      } else {
        localStorage.removeItem("customerAddressIds");
      }
      setIsSubmittingCustomer(false);
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
      const selectedPriceListId = result?.sPriceListId ?? saleTypeData?.customer?.sPriceListId;
      // Patch invoiceAddresses / deliveryAddresses / localisation with the
      // updated customerAddress fields. The upsertB2CCustomer response only
      // returns the single customerAddress, but the cart customer holds the
      // full invoiceAddresses[]/deliveryAddresses[]/localisation[] arrays.
      // Without this patch, view/edit reads stale region/city/etc from those
      // arrays after an update. (Issue #15)
      const patchAddressArray = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0 || !result?.customerAddress) return arr;
        const updatedFirst = {
          ...arr[0],
          line1: result.customerAddress.line1 ?? arr[0].line1,
          line2: result.customerAddress.line2 ?? arr[0].line2,
          city: result.customerAddress.city ?? arr[0].city,
          regionName: result.customerAddress.region ?? arr[0].regionName,
          countryName: result.customerAddress.country ?? arr[0].countryName,
          pincode: result.pincode ?? arr[0].pincode,
        };
        return [updatedFirst, ...arr.slice(1)];
      };
      const patchedInvoiceAddresses = patchAddressArray(customer?.invoiceAddresses);
      const patchedDeliveryAddresses = patchAddressArray(customer?.deliveryAddresses);

      // Extract customerProperties from response metaData if present.
      // The upsertB2C response encodes PAN validation results inside
      // metaData[{key:"Customer Properties", value:"...JSON..."}].
      // Without extracting this, cart.customer.customerProperties stays
      // stale and TCS / PAN status checks fail. (Issues #3, #9, #11)
      let updatedCustomerProperties = customer?.customerProperties || data?.customerProperties;
      if (Array.isArray(updatedMetaData)) {
        const cpEntry = updatedMetaData.find((m) => m.key === "Customer Properties");
        if (cpEntry?.value) {
          try {
            const parsed = typeof cpEntry.value === "string" ? JSON.parse(cpEntry.value) : cpEntry.value;
            if (parsed && typeof parsed === "object") {
              updatedCustomerProperties = { ...(updatedCustomerProperties || {}), ...parsed };
            }
          } catch (e) {
            /* keep existing */
          }
        }
      }
      const customerAddressIds =
        window.location.pathname.includes("/estimate") && localStorage.getItem("customerAddressIds") ? JSON.parse(localStorage.getItem("customerAddressIds")) : null;
      const updatedCustomer = {
        ...result,
        newB2bCustomerId: newB2bCustomerId,
        sCustomer: {
          ...result?.sCustomer,
          sCustomerID: data?.b2bCustomerId || result?.sCustomer?.sCustomerID || null,
        },
        // Use response values with fallback to old cart values. Previously
        // these were unconditionally set to old values, so response updates
        // for loyalty, credit, wallet, etc. were silently discarded.
        // (Issues #2, #3, #5, #9, #13)
        customerProperties: updatedCustomerProperties,
        loyaltyLevel: result?.loyaltyLevel ?? customer?.loyaltyLevel,
        b2cCustomerSegment: result?.b2cCustomerSegment ?? customer?.b2cCustomerSegment,
        creditApproachPercentage: result?.creditApproachPercentage ?? customer?.creditApproachPercentage,
        creditBalance: result?.creditBalance ?? customer?.creditBalance,
        creditLimit: result?.creditLimit ?? customer?.creditLimit,
        cwrWallet: customer?.cwrWallet,
        iscredit: result?.iscredit ?? customer?.iscredit,
        priceListName: result?.priceListName ?? customer?.priceListName,
        lastBillingAmount: result?.lastBillingAmount ?? customer?.lastBillingAmount,
        lastVisitDate: result?.lastVisitDate ?? customer?.lastVisitDate,
        noOfVisits: result?.noOfVisits ?? customer?.noOfVisits,
        retlLoyaltyBalance: result?.retlLoyaltyBalance ?? customer?.retlLoyaltyBalance,
        validity: result?.validity ?? customer?.validity,
        loyaltyPointToExpire: result?.loyaltyPointToExpire ?? customer?.loyaltyPointToExpire,
        // Prefer the user-typed values over the server response — fixes issue
        // #4 where the server's response sometimes had a stale `name` (e.g.
        // because firstName/lastName were re-joined server-side) leaving the
        // UI showing the old name even though the user just typed a new one.
        firstName: data?.firstName || result.firstName,
        lastName: data?.lastName || result.lastName,
        name: data?.name || data?.customerName || data?.editName || result.name || result.firstName || selectedEditOldCustomer?.name || customer?.name,
        email: data?.email || data?.editEmail || result.email,
        customerAddress: result.customerAddress,
        invoiceAddresses: patchedInvoiceAddresses ?? customer?.invoiceAddresses,
        deliveryAddresses: patchedDeliveryAddresses ?? customer?.deliveryAddresses,
        birthday: result.birthday,
        mobileNo: result.mobileNo,
        pincode: result.pincode,
        anniversaryDate: result.anniversaryDate,
        gender: result.gender,
        metaData: result.metaData,
        sPriceListId: selectedPriceListId,
        nriBilling: isNriBilling,
        ...(window.location.pathname.includes("/estimate") && localStorage.getItem("isB2bEnabled") === "true" ? { isB2bEnabled: "Y" } : {}),
        ...(customerAddressIds ? { customerAddressIds } : {}),
      };
      if (isB2BEnabled) {
        updatedCustomer.groupedMetaData = groupedMetaData;
      } else {
        delete updatedCustomer.groupedMetaData;
      }
      if (useTransactionStore.getState().refundCustomerEdit === "Y" && cart?.isReturn === true) {
        useTransactionStore.getState().setRefundReturnCustomerId(result);

        useTransactionStore.getState().setRefundCustomerEdit(null);
        setIsRefundWalletOpen(true); // reopen wallet drawer
        closeCustomerSearch();
        return;
      }
      const isExchangeType = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      const baseCart = isExchangeType ? useCartStore.getState().exchangeCart || cart : cart;
      const updatedCart = {
        ...baseCart,
        customer: { ...updatedCustomer, newB2bCustomerId: selectedB2BCustomer?.customerId || baseCart?.customer?.sCustomer?.sCustomerID || baseCart?.customer?.newB2bCustomerId },
      };
      if (updatedCart.items?.length > 0) {
        const precision = useAuthStore.getState().tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
        const isInterState = getIsInterState(updatedCart);
        updatedCart.groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState });
      }
      if (isExchangeType) {
        if (typeof setExchangeCartTcs === "function") setExchangeCartTcs(true);
        useCartStore.getState().setExchangeCart(updatedCart);
      } else {
        setCart(updatedCart);
      }
      // Show success message BEFORE closing the drawer so user sees feedback
      message.success(result.message || "Customer updated successfully");
      closeCustomerSearch();
      setIsNriBilling(false);
      setIsCreditSale(false);
      upsertPOSLog(updatedCart, "CSU");
    } else {
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      setPanCustomerInfo(null);
      message.error(t("customer_edit_error"));
    }
  } catch (error) {
    Sentry.captureException(new Error("Update customer failed"), {
      extra: newRequest,
    });
    setLastSearchedValue("");
    if (typeof setGstInfo === "function") setGstInfo(null);
    if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
    if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
    setPanCustomerInfo(null);
    console.error("Updating customer failed: ", error);
    message.error(t("customer_edit_error"));
  }
};

export const getCustomer = async (
  form,
  tillLayout,
  customerSearchInput,
  setKioskUI,
  setCustomerSearchResults,
  kioskLogin,
  tillData,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit
) => {
  let setAuthTokens;
  const authHeaders = await getOAuthHeaders();
  if (authHeaders && authHeaders.access_token) {
    setAuthTokens = authHeaders.access_token;
  }
  const posConfig = usePOSConfigStore.getState().posConfig;
  const serverUrl = process.env.REACT_APP_serverUrl;
  let query;
  let validation;
  let loginType;
  if (tillLayout !== 3) {
    if (
      !/^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/.test(customerSearchInput) &&
      !RegExp("^[6-9][0-9]{9}$")?.test(parseFloat(customerSearchInput)) &&
      !parseFloat(customerSearchInput)
    ) {
      validation = RegExp("^[0-9a-zA-Z]{1,20}$");
      loginType = "name";
    }
  }

  if (/^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/.test(customerSearchInput)) {
    loginType = "email";
  }

  if (/^[1-9]\d*$/.test(customerSearchInput)) {
    loginType = "mobile";
  }

  const paramsInput = {
    query: `query{
          customerSearch(searchKey:"${customerSearchInput}",bunitId:"${tillData?.tillAccess?.csBunit?.csBunitId}"){
            cwrCustomerId
            sCustomerAddress
            localisation
            sPriceListId 
            code   
            name
            email        
            mobileNo
            sCustomerAddress
            cardNumber
            regType
            validity
            name
            creditLimit
            creditBalance
            creditApproachPercentage
            pincode
            retlLoyaltyBalance
            b2cRegisteredstoreId
             iscredit
             lastVisitDate
             noOfVisits
             lastBillingAmount
             averageBasketValue
             loyaltyPointToExpire
             firstName
              lastName
               gender
                taxId
            anniversaryDate
             birthday
            loyaltyLevel{
                cwrLoyaltyLevelId
                name
                accumulationRate
                redemptionRate
            }
            cwrWallet {
                 wallet_Id
                balance_Amt
            }
            sCustomer{
              sCustomerID
              customerCategory{
                sCustomerCateforyId
                value
                name
                description
              }
            }
            customerAddress {
                line1 
                line2 
                city 
                region
                 country 
                 csCountryId
                  csRegionId
            }
            csBunit{
              csBunitId
              name
            }
            b2cCustomerSegment{
              name
              cwrB2CCustomerSegmentId
            }
            sPriceListId
            priceListName
            metaData {
              key
              value
            }
          }
        }`,
  };
  try {
    const response = await getCustomerService(paramsInput);
    // Axios({
    //   url: serverUrl,
    //   method: "POST",
    //   data: paramsInput,
    //   headers: {
    //     "Content-Type": "application/json",
    //     Authorization: `${setAuthTokens}`,
    //   },
    // });
    localStorage.setItem("loginType", loginType);
    const customerSearchResults = response?.data?.data?.customerSearch;

    if (customerSearchResults?.length > 0) {
      try {
        const { retailCustomerFields, retailCustomerGroups, retailCustomerTabs } = getFreshConfig();

        // ✅ Get all Business tab IDs
        const businessTabIds = retailCustomerTabs.filter((tab) => tab.name?.toLowerCase().includes("business")).map((tab) => tab.id);

        // ✅ Get all Business field names
        const businessFieldNames = retailCustomerFields.filter((field) => businessTabIds.includes(field.tabId)).map((field) => field.name);

        // ✅ Get all Business group IDs
        const businessGroupIds = retailCustomerGroups.filter((group) => businessTabIds.includes(group.tabId)).map((group) => group.id);

        // ✅ Normalize function
        const normalizeKey = (key) => key?.toLowerCase().replace(/[^a-z0-9]/g, "");

        // ✅ Build remove keys set
        const removeKeysSet = new Set([...businessFieldNames, ...businessGroupIds].map(normalizeKey));

        // ✅ Filter metadata safely
        const cleanedResults = customerSearchResults.map((customer) => {
          const filteredMetaData = (customer.metaData || []).filter((item) => {
            const normalizedItemKey = normalizeKey(item.key);
            return !removeKeysSet.has(normalizedItemKey);
          });

          return {
            ...customer,
            metaData: filteredMetaData,
          };
        });
        setCustomerSearchResults(cleanedResults);

        if (tillLayout === 3) {
          kioskLogin.resetFields();
          setKioskUI(3);

          const customEvent = new CustomEvent("customStorageChange", {
            detail: { key: "kioskUI", newValue: 3 },
          });

          window.dispatchEvent(customEvent);
          localStorage.setItem("kioskUI", 3);
        }
      } catch (error) {
        console.error("Error filtering business metadata:", error);
        setCustomerSearchResults(customerSearchResults);
      }
    } else {
      setCustomerSearchResults([]);

      if (tillLayout === 3) {
        form.setFieldsValue({
          [loginType]: customerSearchInput,
        });
        setKioskUI(4);
        const customEvent = new CustomEvent("customStorageChange", {
          detail: { key: "kioskUI", newValue: 4 },
        });
        window.dispatchEvent(customEvent);
        localStorage.setItem("kioskUI", 4);
      }
    }
    return response;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Customer search failed:", error);
  }
};
